import { Card, CardContent, Box, Typography, Chip, useTheme, alpha } from '@mui/material';
import React, { useCallback, useState } from 'react';
import { BoxType, createShipmentContextType } from '../../../../types/shipment';
import CreateShipmentContext from '../../../../context/CreateShipment';
import { ConfirmBoxDeleteType } from '../Type';
import Boxes from '../BoxWise';
import { defaultBoxState } from '../../constants';
import SbButton from '../../../../components/Button';
import BoxDelete from '../../../GetRatesV2/BoxWise/BoxDelete/BoxDelete';
import { ScrollableBox } from '../../styles';
import updateBoxId from '@Services/updateBoxId';
import { AddressType } from '@Reducers/GetRates/Type';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch, RootStateType } from '@Store';
import { SHIPMENT_TYPES } from '../../../GetRatesV2/Constant';
import { getChargeableWeight } from '@Reducers/GetRates/actions';
import AddIcon from '@mui/icons-material/Add';
import AllInboxOutlinedIcon from '@mui/icons-material/AllInboxOutlined';

function getAddressObj({ pincode, city, country, state }: AddressType) {
  return { city, state, country, pincode };
}

function BoxList({ writeAccess }: { writeAccess?: boolean }) {
  const theme = useTheme();
  const { boxes, setBoxes, selectedBox, setSelectedBox, shipmentType } = React.useContext(
    CreateShipmentContext,
  ) as createShipmentContextType;
  const { shipmentById } = useSelector((state: RootStateType) => state.adminShipment);
  const dispatch = useDispatch<AppDispatch>();

  const [openConfirmBoxDelete, setOpenConfirmBoxDelete] = useState<ConfirmBoxDeleteType>({ open: false, box: null });
  const [openCopyBox, setOpenCopyBox] = useState<ConfirmBoxDeleteType>({ open: false, box: null });

  const writePermission = writeAccess ?? true;

  function handleAddBox() {
    const boxesTemp = [...boxes];
    const box = structuredClone(defaultBoxState);
    box['id'] = boxes.length.toString();
    boxesTemp.push(box);
    setSelectedBox(box);
    const arr = updateBoxId<BoxType>(boxesTemp, defaultBoxState, 0);
    setBoxes([...arr]);
  }

  function onDelete(box: BoxType) {
    if (boxes.length < 2) return;
    setOpenConfirmBoxDelete({ open: true, box });
  }

  const handleCloseModal = useCallback(() => {
    if (openConfirmBoxDelete.open) setOpenConfirmBoxDelete({ open: false, box: null });
    if (openCopyBox.open) setOpenCopyBox({ open: false, box: null });
  }, [openConfirmBoxDelete, openCopyBox]);

  const handleDeleteBox = useCallback(() => {
    handleCloseModal();
    const selectedBoxToDelete = openConfirmBoxDelete.box;
    if (selectedBoxToDelete === null) return;
    let boxesTemp = [...boxes];
    const selectedBoxInd = boxesTemp.findIndex(box => box.id === selectedBoxToDelete.id);
    boxesTemp.splice(selectedBoxInd, 1);
    boxesTemp = boxesTemp.map((box, ind) => ({ ...box, id: ind.toString() }));
    if (selectedBox.id === selectedBoxInd.toString()) {
      setSelectedBox(boxesTemp[0]);
    }
    const arr = updateBoxId<BoxType>(boxesTemp, defaultBoxState, 0);
    setBoxes([...arr]);

    if (shipmentById?.shipmentNumber) {
      const obj = {
        destination: { ...getAddressObj(shipmentById?._consigneeId) },
        source: { ...getAddressObj(shipmentById?._shipperId) },
        package: {
          box: arr.map(box => ({
            weight: box.weight.toString(),
            length: Number(box.length),
            width: Number(box.width),
            height: Number(box.height),
          })),
          unitOfMeasurement: shipmentType.unit,
        },
        document: shipmentType.type === SHIPMENT_TYPES[1].value,
        _courierId: shipmentById?.sellingPricing?._courierId,
        shipmentEdit: true,
        inbound: shipmentById?.sellingPricing?.inbound,
        outbound: shipmentById?.sellingPricing?.outbound,
      };
      dispatch(getChargeableWeight({ ...obj }));
    }
  }, [openConfirmBoxDelete, handleCloseModal]);

  return (
    <>
      <BoxDelete open={openConfirmBoxDelete.open} onClose={handleCloseModal} onConfirm={handleDeleteBox} />

      <Card
        elevation={0}
        sx={{
          borderRadius: '14px',
          border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
          boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.04), 0 2px 8px -2px rgba(0, 0, 0, 0.03)',
          bgcolor: theme.palette.background.paper,
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        {/* Card Header */}
        <Box
          sx={{
            p: { xs: 1.5, sm: 2 },
            borderBottom: `1px solid ${alpha(theme.palette.divider, 0.06)}`,
            bgcolor: alpha(theme.palette.primary.main, 0.02),
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            flexWrap: 'wrap',
            gap: 1,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
            <Box
              sx={{
                width: 34,
                height: 34,
                borderRadius: '8px',
                bgcolor: alpha(theme.palette.primary.main, 0.1),
                color: 'primary.main',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <AllInboxOutlinedIcon sx={{ fontSize: 20 }} />
            </Box>
            <Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography variant="h6" sx={{ fontWeight: 700, color: 'text.primary', fontSize: '1.05rem' }}>
                  Boxes
                </Typography>
                <Chip
                  label={`${boxes.length} Box${boxes.length > 1 ? 'es' : ''}`}
                  size="small"
                  sx={{
                    height: 20,
                    fontSize: '0.6875rem',
                    fontWeight: 700,
                    bgcolor: alpha(theme.palette.primary.main, 0.1),
                    color: 'primary.main',
                  }}
                />
              </Box>
            </Box>
          </Box>

          <SbButton
            size="small"
            variant="contained"
            onClick={handleAddBox}
            startIcon={<AddIcon />}
            disabled={!writePermission ? !writePermission : shipmentType.type !== 'parcel'}
            sx={{
              borderRadius: '8px',
              fontSize: '0.8rem',
              py: 0.5,
              px: 1.5,
              textTransform: 'none',
              fontWeight: 700,
            }}
          >
            Add Box
          </SbButton>
        </Box>

        {/* Box List Body */}
        <CardContent sx={{ p: { xs: 1.5, sm: 2 }, flexGrow: 1 }}>
          <ScrollableBox container>
            {boxes?.map((box: BoxType, i: number) => (
              <Boxes
                key={i}
                boxData={box}
                handleDeleteBox={() => onDelete(box)}
                writePermission={writePermission}
                index={i}
              />
            ))}
          </ScrollableBox>
        </CardContent>
      </Card>
    </>
  );
}

export default BoxList;
